import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import { useStudents } from 'context/StudentsContext';

const gradeLabel = (grade) => {
  if (grade === null || grade === undefined || grade === '') return '';
  if (grade === 'pre-k') return 'Pre-K';
  if (grade === 'k') return 'Kindergarten';
  const n = Number(grade);
  if (!Number.isNaN(n)) {
    const suffix = n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th';
    return `${n}${suffix} Grade`;
  }
  return String(grade);
};

const RecentStudents = () => {
  const navigate = useNavigate();
  const { students, loading, isLive } = useStudents();

  const recent = useMemo(() => {
    const withTimestamp = students.map((s) => ({
      ...s,
      _ts: s.lastUpdated || s.createdAt || 0,
    }));
    withTimestamp.sort((a, b) => String(b._ts).localeCompare(String(a._ts)));
    return withTimestamp.slice(0, 4);
  }, [students]);

  const handleStudentClick = (studentId) => {
    navigate(`/student-profile-management/${encodeURIComponent(studentId)}`);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Recent Students</h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/student-profile-management')}
          iconName="ArrowRight"
          iconPosition="right"
          iconSize={14}
        >
          View All
        </Button>
      </div>

      {loading && recent.length === 0 ? (
        <div className="py-10 text-center text-sm text-gray-500">
          <Icon
            name="Loader2"
            size={20}
            className="mx-auto mb-2 animate-spin text-gray-400"
          />
          Loading students…
        </div>
      ) : recent.length === 0 ? (
        <div className="py-8 text-center">
          <Icon
            name="Users"
            size={28}
            className="mx-auto mb-2 text-gray-300"
          />
          <p className="text-sm text-gray-600">No students yet.</p>
          <p className="mt-1 text-xs text-gray-500">
            {isLive
              ? 'Add your first student to see them here.'
              : 'Demo mode: add a student and it will be stored in this browser only.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {recent.map((student) => (
            <div
              key={student.id}
              onClick={() => handleStudentClick(student.id)}
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors duration-200"
            >
              <div className="relative">
                {student.photo ? (
                  <Image
                    src={student.photo}
                    alt={`${student.firstName} ${student.lastName}`}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-700">
                    {(student.firstName?.[0] || '?').toUpperCase()}
                    {(student.lastName?.[0] || '').toUpperCase()}
                  </div>
                )}
                <div
                  className={`absolute -bottom-1 -right-1 w-2 h-2 rounded-full border-2 border-white ${
                    student.status === 'needs-attention'
                      ? 'bg-red-500'
                      : 'bg-green-500'
                  }`}
                />
              </div>

              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 text-sm truncate">
                  {student.firstName} {student.lastName}
                </p>
                <p className="text-xs text-gray-500">
                  {gradeLabel(student.grade)}
                </p>
              </div>

              <Icon name="ChevronRight" size={14} className="text-gray-400" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecentStudents;
